'use client';

import { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useContent } from '@/contexts/ContentContext';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarPage() {
  const { posts } = useContent();
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [year, setYear] = useState(() => new Date().getFullYear());

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(i);
    return days;
  }, [year, month]);

  const today = new Date();
  const isToday = (day: number) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  // Map posts to dates
  const postsByDate = useMemo(() => {
    const map = new Map<number, typeof posts>();
    posts.forEach(p => {
      if (p.created_at) {
        const d = new Date(p.created_at * 1000);
        if (d.getMonth() === month && d.getFullYear() === year) {
          const day = d.getDate();
          if (!map.has(day)) map.set(day, []);
          map.get(day)!.push(p);
        }
      }
    });
    return map;
  }, [posts, month, year]);

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold text-white">Content Calendar</h1>
        <p className="text-sm text-gray-500 mt-1">View scheduled content</p>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={prev} className="p-2 rounded-lg bg-white/[0.04] hover:bg-gray-800 text-gray-400"><ChevronLeft className="w-4 h-4" /></button>
        <h2 className="text-lg font-medium text-white">
          {new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h2>
        <button onClick={next} className="p-2 rounded-lg bg-white/[0.04] hover:bg-gray-800 text-gray-400"><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-7 gap-px bg-[rgb(var(--muted))]/30 rounded-xl overflow-hidden border border-white/[0.08]">
        {DAYS.map(d => (
          <div key={d} className="px-2 py-2 text-xs font-medium text-gray-500 text-center bg-[rgb(var(--background))]">{d}</div>
        ))}
        {calendarDays.map((day, i) => (
          <div key={i} className={`min-h-[80px] p-1.5 ${day ? 'bg-[rgb(var(--background))]/80' : 'bg-[rgb(var(--background))]/40'} ${day && isToday(day) ? 'ring-1 ring-inset ring-cyan-500/40' : ''}`}>
            {day && (
              <>
                <span className={`text-xs ${isToday(day) ? 'text-cyan-400 font-bold' : 'text-gray-500'}`}>{day}</span>
                {postsByDate.get(day)?.map(p => (
                  <div key={p.id} className="mt-1 px-1.5 py-0.5 rounded bg-cyan-500/10 text-[10px] text-cyan-400 truncate">
                    {p.topic || p.hook || 'Post'}
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
